// Shared Monday-first, weekday-aligned calendar grid builder — the single
// source of truth for calendar layout across Rate Shopper, Price
// Intelligence, and Revenue Manager (RMS). Every calendar-style view in
// these modules must import from here rather than hand-rolling its own
// weekday order, so "Monday first" stays a guardrail, not a per-screen
// convention that can silently drift.
//
// UTC-only date math throughout (Date.UTC / getUTCDay), never
// `new Date(isoString)` local parsing, which shifts the day depending on the
// viewer's timezone (see CLAUDE.md — pg date timezone bug).

export const WEEKDAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Turns a flat list of rows (each carrying an ISO "YYYY-MM-DD" date under
 * `dateKey`) into month grids with leading/trailing blank cells so day 1
 * always lands under its correct Monday-first weekday column.
 *
 * @param {Array<object>} rows
 * @param {string} [dateKey='stay_date']
 * @returns {Array<{ key: string, label: string, cells: Array<object|null> }>}
 *   Each cell is: null (padding outside the month), the real row (when
 *   present), or `{ [dateKey]: dateStr, _outsideHorizon: true }` for a real
 *   calendar day that simply has no data in `rows`.
 */
export function buildMonthGrids(rows, dateKey = 'stay_date') {
  const byDate = new Map((rows || []).map((r) => [r[dateKey], r]));
  const monthKeys = [];
  const seen = new Set();
  for (const r of rows || []) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(r[dateKey] || '');
    if (!m) continue;
    const key = `${m[1]}-${m[2]}`;
    if (!seen.has(key)) { seen.add(key); monthKeys.push({ key, year: Number(m[1]), month: Number(m[2]) }); }
  }
  return monthKeys.map(({ key, year, month }) => {
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    // getUTCDay() is 0=Sun..6=Sat; shift so 0=Mon..6=Sun to match WEEKDAY_ABBR.
    const firstWeekday = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push(byDate.get(dateStr) || { [dateKey]: dateStr, _outsideHorizon: true });
    }
    while (cells.length % 7 !== 0) cells.push(null);

    // These screens show a rolling horizon (today .. today+N), not a full
    // historical month, so whole weeks before/after the data being shown are
    // pure padding — trim those leading/trailing blank week-rows. A partial
    // week that mixes real data with a few blank days (e.g. Mon/Tue before a
    // Wednesday horizon start) is kept, so Monday still anchors the leftmost
    // column for every visible row.
    const isBlankCell = (c) => c == null || c._outsideHorizon;
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    while (weeks.length && weeks[0].every(isBlankCell)) weeks.shift();
    while (weeks.length && weeks[weeks.length - 1].every(isBlankCell)) weeks.pop();

    return { key, label: `${MONTH_NAMES[month - 1]} ${year}`, cells: weeks.flat() };
  });
}
